import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { Search, FileSpreadsheet, Download, ListOrdered, RefreshCw, BookOpen, ArrowRight } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Button } from './Button';
import { Spinner } from './Spinner';
import { toast } from '@/state/toast';
import { useAuth } from '@/state/auth';
import { TepReconcilePanel } from './TepReconcilePanel';

interface Props {
  projectId: string;
  snapshotId: string | null;
}

type Preset = 'question' | 'norm-search' | 'tep' | 'extract' | 'matcher';

interface Citation {
  document_title?: string;
  page?: number;
  text_preview?: string;
  score?: number;
}
interface QueryResponse {
  citations: Citation[];
  snapshot?: { id: string };
  answer?: string;
  cached?: boolean;
  cache_created_at?: string;
}

const PRESETS: { id: Preset; label: string; sub: string; icon: typeof Search }[] = [
  { id: 'question',    label: 'Точечный вопрос',     sub: 'Семантический запрос по проекту',    icon: Search },
  { id: 'norm-search', label: 'Поиск нормы',         sub: 'Норматив ↔ упоминание в ПД',         icon: BookOpen },
  { id: 'tep',         label: 'Сверка ТЭП',          sub: 'ТЗ/ТУ ↔ Паспорт ↔ Разделы ПД',       icon: FileSpreadsheet },
  { id: 'extract',     label: 'Извлечь ТЭП',         sub: 'Технико-экономические показатели',   icon: Download },
  { id: 'matcher',     label: 'Ранжирование ответа', sub: 'Похожие замечания (Matcher)',        icon: ListOrdered },
];

const PRESET_TEMPLATES: Record<Preset, string> = {
  question:      '',
  'norm-search': '',
  tep:           'Сверь технико-экономические показатели в Пояснительной записке (ПЗ) и Технологических решениях (ТКР). Перечисли все расхождения с указанием значений.',
  extract:       'Извлеки технико-экономические показатели проекта в структурированном виде: площадь застройки, этажность, ёмкость, мощность, грузонапряжённость, длина и т.п.',
  matcher:       'Найди в проекте похожие замечания на следующее: ', // hint for user
};

export function QueryTab({ projectId, snapshotId }: Props) {
  const [active, setActive] = useState<Preset>('question');
  const [text, setText] = useState('');
  const [useSnapshot, setUseSnapshot] = useState(true);
  const [topK, setTopK] = useState(8);
  const [graphExpand, setGraphExpand] = useState(false);
  const [responseMode, setResponseMode] = useState<'citations_only' | 'synthesized'>('citations_only');
  const [lastQuery, setLastQuery] = useState<string>('');

  // «Ранжирование ответа» (Matcher) — только для админа; остальным пресет скрыт.
  const isAdmin = useAuth((s) => s.user?.default_role === 'admin');
  const visiblePresets = isAdmin ? PRESETS : PRESETS.filter((p) => p.id !== 'matcher');
  useEffect(() => {
    if (!isAdmin && active === 'matcher') setActive('question');
  }, [isAdmin, active]);

  // На выбор preset подставляем шаблон в textarea и подкручиваем top_k+mode
  // под характер запроса: извлечение/сверка требуют больше chunks и синтеза.
  const onPickPreset = (p: Preset) => {
    setActive(p);
    setText(PRESET_TEMPLATES[p] || '');
    // Пресеты с большим скоупом — больше top_k + LLM-синтез по умолчанию.
    if (p === 'tep' || p === 'extract') {
      setTopK(16);
      setResponseMode('synthesized');
    } else if (p === 'matcher') {
      setTopK(10);
      setResponseMode('citations_only');
    } else {
      // 'question' — оставляем дефолт, пользователь сам выбирает
      setTopK(8);
    }
  };

  const run = useMutation({
    mutationFn: (body: { query: string; top_k: number; snapshot_id?: string; graph_expand: boolean; response_mode: 'citations_only' | 'synthesized'; force_refresh?: boolean }) =>
      api.post<QueryResponse>(`/projects/${projectId}/query`, body),
    onSuccess: () => toast.success('Запрос выполнен'),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось выполнить'),
  });

  const submit = (e: FormEvent, forceRefresh = false) => {
    e.preventDefault();
    if (!text.trim()) return;
    setLastQuery(text.trim());
    run.mutate({
      query: text.trim(),
      top_k: topK,
      snapshot_id: useSnapshot && snapshotId ? snapshotId : undefined,
      graph_expand: graphExpand,
      response_mode: responseMode,
      ...(forceRefresh ? { force_refresh: true } : {}),
    });
  };

  const rerun = () => {
    if (!lastQuery) return;
    run.mutate({
      query: lastQuery,
      top_k: topK,
      snapshot_id: useSnapshot && snapshotId ? snapshotId : undefined,
      graph_expand: graphExpand,
      response_mode: responseMode,
      force_refresh: true,
    });
  };

  const currentPreset = PRESETS.find((p) => p.id === active)!;

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h3 className="font-heading text-ink" style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>Запрос по проекту</h3>
        <p className="text-muted mt-1" style={{ fontSize: 13 }}>
          Семантический поиск, проверки соответствия, извлечение характеристик. Все запросы — через RAG с цитированием норм.
        </p>
      </header>

      <div className="grid grid-cols-12" style={{ gap: 24 }}>
        <aside className="col-span-3">
          <div className="bg-surface rounded-card" style={{ padding: 16, border: '1px solid #E5E7EB' }}>
            <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600, paddingBottom: 8 }}>Тип запроса</div>
            <div className="flex flex-col" style={{ gap: 4 }}>
              {visiblePresets.map((p) => {
                const Icon = p.icon;
                const isActive = active === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onPickPreset(p.id)}
                    className="text-left rounded-btn flex items-start"
                    style={{
                      padding: '12px 14px', gap: 10,
                      background: isActive ? '#FFFFFF' : 'transparent',
                      border: isActive ? '1px solid #E5E7EB' : '1px solid transparent',
                      color: isActive ? '#0A1628' : '#6B7280',
                      fontWeight: isActive ? 600 : 500,
                      fontSize: 14, cursor: 'pointer',
                    }}
                  >
                    <Icon size={18} stroke={isActive ? '#F59E0B' : 'currentColor'} className="shrink-0 mt-0.5" />
                    <div style={{ flex: 1 }}>
                      <div>{p.label}</div>
                      <div className="text-muted" style={{ fontSize: 11, fontWeight: 500, marginTop: 2 }}>{p.sub}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        <section className="col-span-9">
          <div className="bg-white rounded-card border border-border" style={{ overflow: 'hidden' }}>
            <header className="border-b border-border" style={{ padding: '16px 24px' }}>
              <h4 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>{currentPreset.label}</h4>
              <p className="text-muted mt-1" style={{ fontSize: 12 }}>{currentPreset.sub}</p>
            </header>

            {active === 'tep' ? (
              <div style={{ padding: 24 }}>
                {/* Экспертский вид: оба среза ТЭП v2 подряд (требования + ТЭП объекта) */}
                <TepReconcilePanel projectId={projectId} mode="requirements" />
                <TepReconcilePanel projectId={projectId} mode="passport" />
              </div>
            ) : active === 'norm-search' ? (
              <NormAndProjectSearchPanel projectId={projectId} snapshotId={snapshotId} />
            ) : active === 'matcher' ? (
              <MatcherPanel projectId={projectId} snapshotId={snapshotId} />
            ) : (
            <>
            <form onSubmit={submit} style={{ padding: 24 }}>
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Запрос</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="Например: какие требования предъявляются к радиусу кривой в плане для путей категории 2?"
                className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
                style={{ padding: '10px 14px', fontSize: 14, fontFamily: 'Inter, system-ui, sans-serif' }}
              />

              <div className="grid grid-cols-3 mt-4" style={{ gap: 16 }}>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Редакция</label>
                  <select
                    value={useSnapshot ? 'current' : 'all'}
                    onChange={(e) => setUseSnapshot(e.target.value === 'current')}
                    className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
                    style={{ padding: '8px 12px', fontSize: 13 }}
                    disabled={!snapshotId}
                  >
                    <option value="current">{snapshotId ? `Текущий (${snapshotId.slice(0, 8)})` : 'нет редакции'}</option>
                    <option value="all">Все редакции</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Top-K</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={topK}
                    onChange={(e) => setTopK(Math.max(1, Math.min(50, +e.target.value || 8)))}
                    className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink tabular"
                    style={{ padding: '8px 12px', fontSize: 13 }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Режим</label>
                  <select
                    value={responseMode}
                    onChange={(e) => setResponseMode(e.target.value as 'citations_only' | 'synthesized')}
                    className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
                    style={{ padding: '8px 12px', fontSize: 13 }}
                  >
                    <option value="citations_only">Цитаты</option>
                    <option value="synthesized">Синтез (LLM)</option>
                  </select>
                </div>
              </div>

              <label className="inline-flex items-center text-muted mt-3" style={{ fontSize: 12, gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={graphExpand} onChange={(e) => setGraphExpand(e.target.checked)} style={{ accentColor: '#F59E0B', width: 14, height: 14 }} />
                Расширить через граф знаний (Neo4j)
              </label>

              <div className="flex items-center justify-end mt-4">
                <Button type="submit" variant="primary" size="md" icon={<Search size={16} />} disabled={!text.trim() || run.isPending}>
                  {run.isPending ? 'Выполнение…' : 'Выполнить запрос'}
                </Button>
              </div>
            </form>

            {(run.data || run.isPending) && (
              <div className="bg-surface border-t border-border" style={{ padding: '16px 24px' }}>
                <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>Результат запроса</div>
                {run.isPending && (
                  <div className="text-center" style={{ padding: '24px 0' }}><Spinner size={24} className="text-muted" /></div>
                )}
                {run.data && <ResultsList data={run.data} onRerun={rerun} isRerunning={run.isPending} />}
              </div>
            )}
            </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function ResultsList({ data, onRerun, isRerunning }: { data: QueryResponse; onRerun?: () => void; isRerunning?: boolean }) {
  const citations = useMemo(() => data.citations || [], [data.citations]);
  return (
    <div className="mt-3">
      {/* Cache badge */}
      {data.cached && (
        <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: 'rgba(16, 185, 129, 0.10)', color: '#059669',
              border: '1px solid rgba(16, 185, 129, 0.25)',
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />
            из кэша
            {data.cache_created_at && (
              <span style={{ fontWeight: 400, color: '#6B7280', marginLeft: 4 }}>
                · {new Date(data.cache_created_at).toLocaleDateString('ru-RU')}
              </span>
            )}
          </span>
          {onRerun && (
            <button
              type="button"
              onClick={onRerun}
              disabled={isRerunning}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: 'transparent', color: '#6B7280',
                border: '1px solid #E5E7EB',
                opacity: isRerunning ? 0.5 : 1,
              }}
            >
              <RefreshCw size={11} />
              Пересчитать
            </button>
          )}
        </div>
      )}

      {data.answer && (
        <div className="bg-white border border-border rounded-card" style={{ padding: 16, marginBottom: 12 }}>
          <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>Ответ (синтез LLM)</div>
          <p className="text-ink mt-2" style={{ fontSize: 14, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{data.answer}</p>
        </div>
      )}
      {citations.length === 0 && !data.answer && (
        <p className="text-muted text-center" style={{ padding: '16px 0', fontSize: 13 }}>Ничего не найдено</p>
      )}
      <div className="flex flex-col" style={{ gap: 8 }}>
        {citations.slice(0, 10).map((c, i) => (
          <div key={i} className="bg-white border border-border rounded-card" style={{ padding: '12px 16px' }}>
            <div className="flex items-center justify-between">
              <div className="font-heading text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
                {c.document_title || 'Документ'}
              </div>
              <span className="rounded-pill bg-amber/10 tabular" style={{ padding: '2px 8px', fontSize: 11, fontWeight: 600, color: '#B45309', background: 'rgba(245,158,11,.1)' }}>
                {c.score ? `Релевантность ${c.score.toFixed(2)}` : `#${i + 1}`}
              </span>
            </div>
            <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
              {c.page ? `стр.${c.page}` : 'без страницы'}
            </div>
            {c.text_preview && (
              <p className="text-ink mt-2" style={{ fontSize: 13, lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {c.text_preview}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Поиск нормы: двухпанельный режим ────────────────────────────────────
// Слева: поиск в каталоге нормативов (POST /norms/query)
// Справа: поиск в проектной документации (POST /projects/{id}/query)
// Кнопка между панелями: «Искать в ПД →» копирует текст найденной нормы
// в правое поле и автоматически запускает поиск.

interface NormHit {
  id: string;
  text: string;
  metadata: {
    norm_code?: string;
    section_number?: string;
    section_title?: string;
    heading_path?: unknown;
    scope?: unknown;
  };
  distance?: number;
}

interface NormsQueryResponse {
  citations?: NormHit[];
  query?: string;
  total?: number;
}

interface ProjectHit {
  document_title?: string;
  page?: number;
  text_preview?: string;
  score?: number;
}

interface ProjectQueryResponse {
  citations?: ProjectHit[];
  answer?: string;
}

function NormAndProjectSearchPanel({ projectId, snapshotId }: { projectId: string; snapshotId: string | null }) {
  const [normQuery, setNormQuery] = useState('');
  const [normCode, setNormCode] = useState<string>('');
  const [pdQuery, setPdQuery] = useState('');

  // Динамический список нормативов — выпадашка показывает то, что реально в БД
  const registryQ = useQuery<{ norms: Array<{ code: string; short_title: string }> }>({
    queryKey: ['norms-registry-for-search'],
    queryFn: () => api.get('/norms'),
    refetchOnWindowFocus: false,
  });
  const availableCodes = registryQ.data?.norms || [];

  const normMut = useMutation({
    mutationFn: () => api.post<NormsQueryResponse>('/norms/query', {
      query: normQuery.trim(),
      norm_code: normCode.trim() || null,
      top_k: 10,
    }),
    onError: (e) => toast.error((e as Error).message || 'Ошибка поиска в нормативах'),
  });

  const [pdMode, setPdMode] = useState<'citations_only' | 'synthesized'>('synthesized');

  const pdMut = useMutation({
    mutationFn: () => api.post<ProjectQueryResponse>(`/projects/${projectId}/query`, {
      query: pdQuery.trim(),
      top_k: 10,
      snapshot_id: snapshotId || undefined,
      graph_expand: false,
      response_mode: pdMode,
    }),
    onError: (e) => toast.error((e as Error).message || 'Ошибка поиска в ПД'),
  });

  const useAsProjectQuery = (text: string) => {
    // Очищаем markdown-разметку для лучшего embedding
    const clean = text.replace(/[#*_`>]/g, '').replace(/\s+/g, ' ').trim().slice(0, 500);
    setPdQuery(clean);
    // Автоматически запускаем поиск через таймаут (state ещё не обновился)
    setTimeout(() => pdMut.mutate(), 50);
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ fontSize: 13, color: '#475569', marginBottom: 16, lineHeight: 1.5 }}>
        Двухпанельный поиск: найдите норму в каталоге слева, скопируйте её в правую панель,
        проверьте упоминается ли это требование в проектной документации.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* ─── Левая панель: нормативы ─── */}
        <div style={{ border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, background: '#F8FAFC' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <BookOpen size={16} color="#4338CA" />
            <h5 style={{ fontSize: 14, fontWeight: 600, color: '#0F172A' }}>Каталог нормативов</h5>
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <select
              value={normCode}
              onChange={(e) => setNormCode(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 12, background: 'white' }}
            >
              <option value="">Все нормативы</option>
              {availableCodes.map(n => (
                <option key={n.code} value={n.code}>{n.code} — {n.short_title}</option>
              ))}
            </select>
          </div>

          <textarea
            value={normQuery}
            onChange={(e) => setNormQuery(e.target.value)}
            placeholder="Например: «требования к расчёту нагрузок и воздействий» или «безопасность при опасных природных воздействиях»"
            rows={3}
            style={{ width: '100%', padding: 10, border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Button
              variant="primary"
              size="sm"
              icon={<Search size={14} />}
              onClick={() => normMut.mutate()}
              disabled={!normQuery.trim() || normMut.isPending}
            >
              {normMut.isPending ? 'Ищу…' : 'Найти в нормативах'}
            </Button>
          </div>

          <div style={{ marginTop: 16, maxHeight: 500, overflowY: 'auto' }}>
            {normMut.isPending && <Spinner size={20} className="text-muted" />}
            {normMut.data?.citations?.length === 0 && (
              <p className="text-muted" style={{ fontSize: 12, padding: 12 }}>Ничего не найдено в каталоге.</p>
            )}
            {(normMut.data?.citations || []).map((hit, i) => (
              <div key={hit.id || i} style={{
                padding: 10, marginBottom: 8, background: 'white', borderRadius: 8,
                border: '1px solid #E2E8F0',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>
                    <span style={{ padding: '1px 6px', borderRadius: 4, background: '#EEF2FF', color: '#4338CA' }}>
                      {hit.metadata?.norm_code || '—'}
                    </span>
                    {hit.metadata?.section_number && (
                      <span style={{ marginLeft: 6, color: '#64748B' }}>{hit.metadata.section_number}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => useAsProjectQuery(hit.text)}
                    title="Скопировать этот фрагмент в поиск по ПД"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 8px', borderRadius: 6,
                      background: '#F59E0B', color: 'white', border: 'none',
                      fontSize: 11, fontWeight: 500, cursor: 'pointer',
                    }}
                  >
                    Искать в ПД <ArrowRight size={11} />
                  </button>
                </div>
                {hit.metadata?.section_title && (
                  <div style={{ fontSize: 11, fontWeight: 500, color: '#475569', marginBottom: 4 }}>
                    {hit.metadata.section_title}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {hit.text.length > 600 ? hit.text.slice(0, 600) + '…' : hit.text}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ─── Правая панель: ПД ─── */}
        <div style={{ border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, background: '#F0FDF4' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FileSpreadsheet size={16} color="#15803D" />
              <h5 style={{ fontSize: 14, fontWeight: 600, color: '#0F172A' }}>Проектная документация</h5>
            </div>
            <select
              value={pdMode}
              onChange={(e) => setPdMode(e.target.value as 'citations_only' | 'synthesized')}
              style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #CBD5E1', borderRadius: 6, background: 'white', color: '#374151' }}
              title="Режим ответа: цитаты (фрагменты документов) или синтез ИИ"
            >
              <option value="synthesized">Синтез ИИ</option>
              <option value="citations_only">Цитаты</option>
            </select>
          </div>

          <textarea
            value={pdQuery}
            onChange={(e) => setPdQuery(e.target.value)}
            placeholder="Перенесите норму из левой панели, чтобы найти упоминания в томах проекта"
            rows={3}
            style={{ width: '100%', padding: 10, border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Button
              variant="primary"
              size="sm"
              icon={<Search size={14} />}
              onClick={() => pdMut.mutate()}
              disabled={!pdQuery.trim() || pdMut.isPending}
            >
              {pdMut.isPending ? 'Ищу…' : pdMode === 'synthesized' ? 'Сверить с нормой' : 'Найти в ПД'}
            </Button>
          </div>

          <div style={{ marginTop: 16, maxHeight: 500, overflowY: 'auto' }}>
            {pdMut.isPending && <Spinner size={20} className="text-muted" />}
            {pdMut.data?.answer && (
              <div style={{ padding: 12, marginBottom: 12, background: '#ECFDF5', borderRadius: 8, border: '1px solid #6EE7B7' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#065F46', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Заключение ИИ
                </div>
                <div style={{ fontSize: 13, color: '#064E3B', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {pdMut.data.answer}
                </div>
              </div>
            )}
            {!pdMut.data?.answer && pdMut.data?.citations?.length === 0 && (
              <p className="text-muted" style={{ fontSize: 12, padding: 12 }}>
                В проектной документации не найдено упоминаний. Возможно, требование нормы не отражено в ПД — это потенциальный пробел комплектности.
              </p>
            )}
            {(pdMut.data?.citations || []).map((hit, i) => (
              <div key={i} style={{
                padding: 10, marginBottom: 8, background: 'white', borderRadius: 8,
                border: '1px solid #E2E8F0',
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#15803D', marginBottom: 4 }}>
                  {hit.document_title || 'Документ'}
                  {hit.page && <span style={{ color: '#64748B', fontWeight: 400 }}> · стр. {hit.page}</span>}
                  {typeof hit.score === 'number' && (
                    <span style={{ marginLeft: 8, color: '#94A3B8', fontWeight: 400 }}>
                      score {hit.score.toFixed(2)}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {hit.text_preview && hit.text_preview.length > 500 ? hit.text_preview.slice(0, 500) + '…' : hit.text_preview}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MatcherPanel: похожие замечания ────────────────────────────────────────
// Вместо поиска чанков ПД — находит семантически близкие замечания в проекте.
// Backend: POST /projects/{id}/snapshots/{sid}/matcher/rank-candidate
interface RankedRemark {
  remark_id: string;
  body: string;
  score: number;
  response_count: number;
}

function MatcherPanel({ projectId, snapshotId }: { projectId: string; snapshotId: string | null }) {
  const [candidate, setCandidate] = useState('');

  const matchMut = useMutation({
    mutationFn: (text: string) =>
      api.post<{ ranked: RankedRemark[]; mode: string; note: string }>(
        `/projects/${projectId}/snapshots/${snapshotId}/matcher/rank-candidate`,
        { candidate_text: text, scope: 'all' },
      ),
    onError: (e) => toast.error((e as Error).message || 'Ошибка поиска'),
  });

  return (
    <div style={{ padding: 24 }}>
      <div style={{ fontSize: 13, color: '#475569', marginBottom: 16, lineHeight: 1.55 }}>
        Введите текст нового замечания или описание проблемы — система найдёт наиболее похожие
        замечания в этом проекте. Используется для проверки дублей и поиска связанных ранее
        зафиксированных проблем.
      </div>

      <textarea
        value={candidate}
        onChange={(e) => setCandidate(e.target.value)}
        rows={4}
        placeholder="Например: «Отсутствует план трассы с указанием знаков севера на листах 1, 2, 6»"
        style={{ width: '100%', padding: '10px 14px', fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 8, fontFamily: 'inherit', resize: 'vertical' }}
        disabled={!snapshotId}
      />

      {!snapshotId && (
        <p className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>Для поиска нужна активная редакция (snapshot).</p>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        <Button
          variant="primary"
          size="sm"
          onClick={() => { if (candidate.trim()) matchMut.mutate(candidate.trim()); }}
          disabled={!candidate.trim() || !snapshotId || matchMut.isPending}
        >
          {matchMut.isPending ? 'Ищу…' : 'Найти похожие замечания'}
        </Button>
      </div>

      {matchMut.isPending && (
        <div className="flex items-center text-muted mt-4" style={{ gap: 8, fontSize: 13 }}>
          <Spinner size={16} /> Анализирую замечания…
        </div>
      )}

      {matchMut.data && (
        <div style={{ marginTop: 20 }}>
          {matchMut.data.ranked.length === 0 ? (
            <p className="text-muted text-center" style={{ padding: '24px 0', fontSize: 14 }}>Похожих замечаний не найдено</p>
          ) : (
            <>
              <div className="text-muted" style={{ fontSize: 11, marginBottom: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                Найдено {matchMut.data.ranked.length} похожих замечаний (ранг по схожести ↓)
              </div>
              {matchMut.data.ranked.slice(0, 10).map((r, i) => {
                const pct = Math.round(r.score * 100);
                const color = pct >= 80 ? '#10B981' : pct >= 60 ? '#F59E0B' : '#9CA3AF';
                return (
                  <div key={r.remark_id} style={{ padding: '12px 14px', marginBottom: 8, borderRadius: 8, border: '1px solid #E5E7EB', background: '#F9FAFB' }}>
                    <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>
                        #{i + 1} · {r.remark_id.slice(0, 8)}
                      </span>
                      <div className="flex items-center" style={{ gap: 8 }}>
                        {r.response_count > 0 && (
                          <span style={{ fontSize: 11, color: '#6B7280' }}>
                            {r.response_count} ответ{r.response_count > 1 ? 'а' : ''}
                          </span>
                        )}
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
                          background: `${color}18`, color,
                        }}>
                          {pct}% схожести
                        </span>
                      </div>
                    </div>
                    <p style={{ fontSize: 13, color: '#0F172A', lineHeight: 1.5, margin: 0, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {r.body}
                    </p>
                  </div>
                );
              })}
              <p className="text-muted" style={{ fontSize: 11, marginTop: 8 }}>
                {matchMut.data.note}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
